import api from '../api/client';

// ============================================================
// Types
// ============================================================

export type ServiceStatus = 'OK' | 'WARNING' | 'OVERDUE' | 'BOOKED' | 'COMPLETED';
export type ServiceCategory = 'MAINTENANCE' | 'TIRE' | 'INSPECTION' | 'CUSTOM';
export type TireType = 'SUMMER' | 'WINTER_FRICTION' | 'WINTER_STUDDED';
export type TireStorageType = 'HOTEL' | 'STORAGE' | 'VEHICLE' | 'OTHER';
export type TireStatus = 'INSTALLED' | 'STORED' | 'WORN' | 'DISPOSED';

export interface ServiceType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: ServiceCategory;
  defaultIntervalKm: number | null;
  defaultIntervalMonths: number | null;
  isSystem: boolean;
}

export interface ServicePoint {
  id: string;
  vehicleId: number;
  serviceTypeId: string;
  organizationId: string;
  source: string;
  intervalKm: number | null;
  intervalMonths: number | null;
  warningKm: number;
  warningDays: number;
  lastPerformedDate: string | null;
  lastPerformedKm: number | null;
  nextDueDate: string | null;
  nextDueKm: number | null;
  status: ServiceStatus;
  bookingDate: string | null;
  bookingWorkshopId: string | null;
  bookingNotes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  serviceTypeName?: string;
  serviceTypeCode?: string;
  serviceTypeCategory?: ServiceCategory;
  workshopName?: string;
  currentKm?: number | null;
  avgKmPerDay?: number | null;
  estimatedDueDate?: string | null;
}

export interface ServiceHistory {
  id: string;
  servicePointId: string;
  vehicleId: number;
  performedDate: string;
  performedKm: number | null;
  costSek: number | null;
  workshopId: string | null;
  notes: string | null;
  workshopName?: string;
  performedByName?: string;
}

export interface VehicleTire {
  id: string;
  vehicleId: number;
  tireType: TireType;
  brand: string | null;
  model: string | null;
  size: string | null;
  storageType: TireStorageType;
  storageLocation: string | null;
  installedDate: string | null;
  installedKm: number | null;
  lastServiceDate: string | null;
  status: TireStatus;
  treadDepthMm: number | null;
  treadDepthVfMm: number | null;
  treadDepthHfMm: number | null;
  treadDepthVbMm: number | null;
  treadDepthHbMm: number | null;
  notes: string | null;
}

export interface TireChangeLogEntry {
  id: number;
  changeDate: string;
  changedAt: string;
  removedTireType: TireType | null;
  removedTireBrand: string | null;
  removedTireSize: string | null;
  removedTreadVfMm: number | null;
  removedTreadHfMm: number | null;
  removedTreadVbMm: number | null;
  removedTreadHbMm: number | null;
  removedTreadMinMm: number | null;
  removedInstalledAt: string | null;
  removedInitialTreadMm: number | null;
  installedTireType: TireType | null;
  installedTireBrand: string | null;
  installedTireSize: string | null;
  installedTreadVfMm: number | null;
  installedTreadHfMm: number | null;
  installedTreadVbMm: number | null;
  installedTreadHbMm: number | null;
  installedTreadMinMm: number | null;
  odometerAtChange: number | null;
  kmDrivenOnRemoved: number | null;
  treadWornVfMm: number | null;
  treadWornHfMm: number | null;
  treadWornVbMm: number | null;
  treadWornHbMm: number | null;
  treadWornAvgMm: number | null;
}

export interface Workshop {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  contactPerson: string | null;
  workshopType?: 'EXTERN' | 'INTERN';
  internalUserId?: string | null;
  internalUserName?: string | null;
}

export interface CalendarDayPoint {
  id: string;
  vehicleId: number;
  regNr: string;
  serviceTypeName: string;
  category: string;
  status: string;
}

export interface CalendarDay {
  date: string;
  count: number;
  byCategory: Record<string, number>;
  points: CalendarDayPoint[];
}

export interface ServicePersonnel {
  id: string;
  name: string;
  email: string;
  organizationName: string;
}

export interface ParsedInvoice {
  supplier_name: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  total_sek: number | null;
  vat_sek: number | null;
  items: Array<{ description: string; quantity: number | null; unit_price: number | null; vat_rate: number | null }>;
}

export interface InvoiceUploadResult {
  invoiceId: string;
  batchId: string;
  parsed: ParsedInvoice;
  aiModel: string;
}

export interface CostReportRow {
  month: string;
  category: string;
  categoryName: string;
  totalSek: number;
  serviceCount: number;
}

export interface ServiceOverviewVehicle {
  vehicleId: number;
  registrationNumber: string;
  brand: string | null;
  model: string | null;
  organizationName: string | null;
  servicePoints: ServicePoint[];
}

export interface ServiceOverviewCount {
  warning: number;
  overdue: number;
  booked: number;
  total: number;
}

// ============================================================
// API calls
// ============================================================

export const serviceApi = {
  // Service Types
  async getServiceTypes(): Promise<ServiceType[]> {
    const res = await api.get('/api/service/types');
    return res.data.data;
  },

  // Service Points
  async getServicePoints(vehicleId: number): Promise<ServicePoint[]> {
    const res = await api.get(`/api/service/vehicle/${vehicleId}`);
    return res.data.data;
  },

  async createServicePoint(vehicleId: number, data: Record<string, any>): Promise<string> {
    const res = await api.post(`/api/service/vehicle/${vehicleId}/points`, data);
    return res.data.data.id;
  },

  async updateServicePoint(pointId: string, data: Record<string, any>): Promise<void> {
    await api.put(`/api/service/points/${pointId}`, data);
  },

  async getInspectionConfirmAddress(
    vehicleId: number,
    dueDate?: string | null
  ): Promise<{ email: string; registrationNumber: string }> {
    const res = await api.get(`/api/service/vehicle/${vehicleId}/inspection/confirm-address`, {
      params: dueDate ? { dueDate } : undefined,
    });
    return res.data.data;
  },

  async bookInspection(
    vehicleId: number,
    data: {
      dueDate?: string | null;
      bookingDate: string;
      bookingTime?: string | null;
      company: string;
      station: string;
      notes?: string | null;
    }
  ): Promise<{ id: number }> {
    const res = await api.post(`/api/service/vehicle/${vehicleId}/inspection/book`, data);
    return res.data.data;
  },

  async completeServicePoint(pointId: string, data: Record<string, any>): Promise<void> {
    await api.put(`/api/service/points/${pointId}/complete`, data);
  },

  // Service History
  async getServiceHistory(vehicleId: number): Promise<ServiceHistory[]> {
    const res = await api.get(`/api/service/vehicle/${vehicleId}/history`);
    return res.data.data;
  },

  // Overview
  async getServiceOverview(): Promise<ServiceOverviewVehicle[]> {
    const res = await api.get('/api/service/overview');
    return res.data.data;
  },

  async getServiceOverviewCount(): Promise<ServiceOverviewCount> {
    const res = await api.get('/api/service/overview/count');
    return res.data.data;
  },

  // Tires
  async getTires(vehicleId: number): Promise<VehicleTire[]> {
    const res = await api.get(`/api/service/vehicle/${vehicleId}/tires`);
    return res.data.data;
  },

  async createTire(vehicleId: number, data: Record<string, any>): Promise<string> {
    const res = await api.post(`/api/service/vehicle/${vehicleId}/tires`, data);
    return res.data.data.id;
  },

  async updateTire(tireId: string, data: Record<string, any>): Promise<void> {
    await api.put(`/api/service/tires/${tireId}`, data);
  },

  async installTire(tireId: string, installedKm?: number): Promise<void> {
    await api.post(`/api/service/tires/${tireId}/install`, { installedKm });
  },

  async storeTire(tireId: string): Promise<void> {
    await api.post(`/api/service/tires/${tireId}/store`);
  },

  async getTireChangeLog(vehicleId: number): Promise<TireChangeLogEntry[]> {
    const res = await api.get(`/api/service/vehicle/${vehicleId}/tires/history`);
    return res.data.data;
  },

  // Workshops
  async getWorkshops(): Promise<Workshop[]> {
    const res = await api.get('/api/service/workshops');
    return res.data.data;
  },

  async createWorkshop(data: Record<string, any>): Promise<string> {
    const res = await api.post('/api/service/workshops', data);
    return res.data.data.id;
  },

  async updateWorkshop(workshopId: string, data: Record<string, any>): Promise<void> {
    await api.put(`/api/service/workshops/${workshopId}`, data);
  },

  // Car.info sync
  async syncCarInfo(vehicleId: number): Promise<number> {
    const res = await api.post(`/api/service/sync-carinfo/${vehicleId}`);
    return res.data.data.synced;
  },

  async syncCarInfoAll(): Promise<{ synced: number; vehicles: number }> {
    const res = await api.post('/api/service/sync-carinfo-all');
    return res.data.data;
  },

  // Bulk operations
  async bulkBook(data: { servicePointIds: string[]; bookingDate: string; workshopId?: string; notes?: string }): Promise<{ updated: number }> {
    const res = await api.post('/api/service/bulk-book', data);
    return res.data.data;
  },

  async bulkComplete(data: {
    servicePointIds: string[];
    performedDate: string;
    performedKm?: number;
    costSek?: number;
    workshopId?: string;
    notes?: string;
    batchId?: string;
    tireType?: string;
    tireBrand?: string;
    tireSize?: string;
    treadDepthVfMm?: number;
    treadDepthHfMm?: number;
    treadDepthVbMm?: number;
    treadDepthHbMm?: number;
  }): Promise<{ updated: number; batchId: string }> {
    const res = await api.post('/api/service/bulk-complete', data);
    return res.data.data;
  },

  // Calendar
  async getCalendar(year: number, month: number): Promise<CalendarDay[]> {
    const res = await api.get('/api/service/calendar', { params: { year, month } });
    return res.data.data;
  },

  // Service personnel
  async getServicePersonnel(): Promise<ServicePersonnel[]> {
    const res = await api.get('/api/service/workshops/service-personnel');
    return res.data.data;
  },

  // Invoice upload + AI parse
  async uploadAndParseInvoice(file: File, batchId?: string): Promise<InvoiceUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (batchId) formData.append('batchId', batchId);
    const res = await api.post('/api/service/invoices/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },

  async getInvoiceDownloadUrl(invoiceId: string): Promise<string> {
    const res = await api.get(`/api/service/invoices/${invoiceId}/download`);
    return res.data.data.url;
  },

  // Cost report
  async getCostReport(fromDate: string, toDate: string): Promise<CostReportRow[]> {
    const res = await api.get('/api/service/reports/costs', { params: { fromDate, toDate } });
    return res.data.data;
  },
};

export default serviceApi;
